import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface StudySession {
  id: string
  subject: string
  topic: string
  duration: number
  date: string
  type: 'reading' | 'practice' | 'review' | 'exam'
  score?: number
}

interface StudyPlan {
  id: string
  title: string
  description: string
  startDate: string
  endDate: string
  subjects: string[]
  dailyGoal: number
  weeklyGoal: number
  isActive: boolean
}

interface StudyStats {
  totalStudyTime: number
  weeklyStudyTime: number
  dailyStudyTime: number
  streakDays: number
  completedSessions: number
  averageScore: number
  subjectProgress: Record<string, number>
}

interface StudyState {
  sessions: StudySession[]
  plans: StudyPlan[]
  currentPlan: StudyPlan | null
  stats: StudyStats
  isStudying: boolean
  currentSession: StudySession | null
  subjects: string[]
  goals: {
    daily: number
    weekly: number
    monthly: number
  }
}

const initialState: StudyState = {
  sessions: [
    {
      id: '1',
      subject: 'Matematik',
      topic: 'Cebirsel İfadeler',
      duration: 45,
      date: '2024-01-15',
      type: 'practice',
      score: 85
    },
    {
      id: '2',
      subject: 'Türkçe',
      topic: 'Okuduğunu Anlama',
      duration: 30,
      date: '2024-01-15',
      type: 'reading',
      score: 92
    },
    {
      id: '3',
      subject: 'Fen Bilimleri',
      topic: 'Kuvvet ve Hareket',
      duration: 60,
      date: '2024-01-14',
      type: 'review',
      score: 78
    }
  ],
  plans: [
    {
      id: '1',
      title: 'LGS Hazırlık Planı',
      description: 'Kapsamlı LGS hazırlık programı',
      startDate: '2024-01-01',
      endDate: '2024-06-15',
      subjects: ['Matematik', 'Türkçe', 'Fen Bilimleri', 'Sosyal Bilgiler', 'İngilizce', 'Din Kültürü'],
      dailyGoal: 120,
      weeklyGoal: 840,
      isActive: true
    }
  ],
  currentPlan: null,
  stats: {
    totalStudyTime: 1250,
    weeklyStudyTime: 320,
    dailyStudyTime: 45,
    streakDays: 12,
    completedSessions: 45,
    averageScore: 82,
    subjectProgress: {
      'Matematik': 75,
      'Türkçe': 85,
      'Fen Bilimleri': 70,
      'Sosyal Bilgiler': 80,
      'İngilizce': 65,
      'Din Kültürü': 90
    }
  },
  isStudying: false,
  currentSession: null,
  subjects: ['Matematik', 'Türkçe', 'Fen Bilimleri', 'Sosyal Bilgiler', 'İngilizce', 'Din Kültürü'],
  goals: {
    daily: 120,
    weekly: 840,
    monthly: 3600
  }
}

const studySlice = createSlice({
  name: 'study',
  initialState,
  reducers: {
    startStudySession: (state, action: PayloadAction<Omit<StudySession, 'id' | 'duration'>>) => {
      const newSession: StudySession = {
        ...action.payload,
        id: Date.now().toString(),
        duration: 0
      }
      state.currentSession = newSession
      state.isStudying = true
    },
    endStudySession: (state, action: PayloadAction<{ duration: number; score?: number }>) => {
      if (state.currentSession) {
        const completedSession = {
          ...state.currentSession,
          duration: action.payload.duration,
          score: action.payload.score
        }
        state.sessions.unshift(completedSession)
        state.stats.totalStudyTime += action.payload.duration
        state.stats.dailyStudyTime += action.payload.duration
        state.stats.weeklyStudyTime += action.payload.duration
        state.stats.completedSessions += 1
        
        if (action.payload.score) {
          const totalScore = state.sessions.reduce((sum, session) => 
            sum + (session.score || 0), 0
          )
          const sessionsWithScore = state.sessions.filter(session => session.score).length
          state.stats.averageScore = Math.round(totalScore / sessionsWithScore)
        }
        
        state.currentSession = null
        state.isStudying = false
      }
    },
    pauseStudySession: (state) => {
      state.isStudying = false
    },
    resumeStudySession: (state) => {
      state.isStudying = true
    },
    addStudyPlan: (state, action: PayloadAction<Omit<StudyPlan, 'id'>>) => {
      const newPlan: StudyPlan = {
        ...action.payload,
        id: Date.now().toString()
      }
      state.plans.push(newPlan)
    },
    updateStudyPlan: (state, action: PayloadAction<StudyPlan>) => {
      const index = state.plans.findIndex(plan => plan.id === action.payload.id)
      if (index !== -1) {
        state.plans[index] = action.payload
      }
    },
    deleteStudyPlan: (state, action: PayloadAction<string>) => {
      state.plans = state.plans.filter(plan => plan.id !== action.payload)
    },
    setCurrentPlan: (state, action: PayloadAction<StudyPlan | null>) => {
      state.currentPlan = action.payload
    },
    updateSubjectProgress: (state, action: PayloadAction<{ subject: string; progress: number }>) => {
      state.stats.subjectProgress[action.payload.subject] = action.payload.progress
    },
    setGoals: (state, action: PayloadAction<{ daily: number; weekly: number; monthly: number }>) => {
      state.goals = action.payload
    },
    incrementStreak: (state) => {
      state.stats.streakDays += 1
    },
    resetStreak: (state) => {
      state.stats.streakDays = 0
    },
    deleteStudySession: (state, action: PayloadAction<string>) => {
      const session = state.sessions.find(s => s.id === action.payload)
      if (session) {
        state.sessions = state.sessions.filter(s => s.id !== action.payload)
        state.stats.totalStudyTime -= session.duration
        state.stats.completedSessions -= 1
      }
    },
    // Firebase senkronizasyon action'ları
    setFirebasePlans: (state, action: PayloadAction<StudyPlan[]>) => {
      state.plans = action.payload
    },
    syncPlanWithFirebase: (state, action: PayloadAction<StudyPlan>) => {
      const index = state.plans.findIndex(plan => plan.id === action.payload.id)
      if (index !== -1) {
        state.plans[index] = action.payload
      } else {
        state.plans.push(action.payload)
      }
    }
  },
})

export const {
  startStudySession,
  endStudySession,
  pauseStudySession,
  resumeStudySession,
  addStudyPlan,
  updateStudyPlan,
  deleteStudyPlan,
  setCurrentPlan,
  updateSubjectProgress,
  setGoals,
  incrementStreak,
  resetStreak,
  deleteStudySession,
  setFirebasePlans,
  syncPlanWithFirebase,
} = studySlice.actions

export default studySlice.reducer